import React, { useMemo } from 'react';
import classNames from 'classnames';
import { channelInfo } from '@op/components/ChannelLabel';
import { LANGUAGE_EN } from '@home/_config/config';
import { selectValByCondition } from '@home/utils/common';
import { useIntl } from 'umi';
import {
  PR_CHANNEL_BRAND_COLORS,
  PR_CHANNEL_LEGEND_DEFAULT_ORDER,
  getBrandColorByChannelKey,
  normalizeApiChannelNameToKey,
} from '@home/pages/studio/pr/prChannelColors';
import './index.scss';

export type TPrChannelColorLegendItem = {
  code: string;
  color: string;
  nameEn?: string;
  nameCn?: string;
};

export interface IPrChannelColorLegendProps {
  className?: string;
  /**
   * Render 注入的页面 data；与列表 Service 的 dataKey 一致（如 prMediaTable），
   * 从 list 接口 formatData 后的 `data[xxx].data.channels` 取色条（与表格列来源一致，不再用 games 接口的 prGamesChannels）。
   */
  data?: Record<string, unknown>;
  /** 对应该 Tab 的列表 Service 的 dataKey，用于读取 `data[key].data.channels` */
  listDataKey?: string;
  /**
   * inline：无标题、置于表格卡标题栏右侧（与下载并列）；block：原独立色带样式（仅调试或特殊页）
   */
  variant?: 'inline' | 'block';
  /** 是否展示顶栏小标题，默认 false（设计稿无「Channels」标题） */
  showTitle?: boolean;
  titleEn?: string;
  titleCn?: string;
  colorByCode?: Record<string, string>;
  order?: string[];
  items?: TPrChannelColorLegendItem[];
}

interface IPrListServiceBlock {
  data?: { channels?: unknown; };
}

/** 列表类接口归一化后 `channels` 在 `data[serviceKey].data` 上 */
function getChannelNamesFromListResponse(
  data: Record<string, unknown> | undefined,
  listDataKey: string | undefined,
): string[] {
  if (!data || !listDataKey) {
    return [];
  }
  const block = data[listDataKey] as IPrListServiceBlock | undefined;
  const ch = block?.data?.channels;
  if (!Array.isArray(ch)) {
    return [];
  }
  return ch.map((c) => String(c)).filter((s) => s.length > 0);
}

function defaultLabelForCodeKey(code: string): string {
  const c = String(code || '').toLowerCase();
  const { name } = channelInfo(c as never, undefined, false);
  return name || code;
}

const PrChannelColorLegend: React.FC<IPrChannelColorLegendProps> = (props) => {
  const {
    className,
    data,
    listDataKey,
    variant = 'inline',
    showTitle = false,
    titleEn = 'Channels',
    titleCn = 'Channels',
    colorByCode: colorByCodeIn,
    order: orderIn,
    items: itemsIn,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const items = useMemo((): TPrChannelColorLegendItem[] => {
    if (itemsIn?.length) {
      return itemsIn;
    }
    const fromList = getChannelNamesFromListResponse(data, listDataKey);
    if (fromList.length) {
      return fromList.map((apiLabel, index) => {
        const key = normalizeApiChannelNameToKey(apiLabel) || `ch_${index}`;
        const color = getBrandColorByChannelKey(apiLabel, index);
        return {
          code: key,
          color,
          nameEn: apiLabel,
          nameCn: apiLabel,
        };
      });
    }
    if (colorByCodeIn && Object.keys(colorByCodeIn).length) {
      const map = { ...PR_CHANNEL_BRAND_COLORS, ...colorByCodeIn };
      const order = (orderIn?.length ? orderIn : PR_CHANNEL_LEGEND_DEFAULT_ORDER).map((c) =>
        String(c).toLowerCase(),
      );
      const out: TPrChannelColorLegendItem[] = [];
      const seen = new Set<string>();
      order.forEach((code) => {
        if (!code || seen.has(code)) return;
        const color = map[code];
        if (!color) return;
        seen.add(code);
        out.push({
          code,
          color,
          nameEn: defaultLabelForCodeKey(code),
          nameCn: defaultLabelForCodeKey(code),
        });
      });
      return out;
    }
    return [];
  }, [data, listDataKey, colorByCodeIn, orderIn, itemsIn]);

  if (!items.length) {
    return null;
  }

  const title = selectValByCondition(isEn, titleEn, titleCn);
  const isInline = variant === 'inline';

  return (
    <div
      className={classNames('dbh-pr-channel-legend', className, {
        'dbh-pr-channel-legend--inline': isInline,
        'dbh-pr-channel-legend--block': !isInline,
      })}
    >
      {showTitle ? (
        <div className="dbh-pr-channel-legend__head">
          <span className="dbh-pr-channel-legend__title">{title}</span>
        </div>
      ) : null}
      <ul className="dbh-pr-channel-legend__list" role="list">
        {items.map((it) => {
          const key = String(it.code).toLowerCase();
          const label = selectValByCondition(
            isEn,
            it.nameEn || defaultLabelForCodeKey(key),
            it.nameCn || it.nameEn || defaultLabelForCodeKey(key),
          );
          return (
            <li key={`${key}-${it.color}`} className="dbh-pr-channel-legend__item" role="listitem">
              <span
                className="dbh-pr-channel-legend__swatch"
                style={{ backgroundColor: it.color }}
                aria-hidden
              />
              <img
                className="dbh-pr-channel-legend__icon"
                src={channelInfo(key as never).img}
                alt={label}
                aria-hidden
              />
              <span className="dbh-pr-channel-legend__label">{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PrChannelColorLegend;
